<template>
  <div class="hypo-workflow-header">
    <mds-layout-grid gutters="none">
      <mds-row align-horizontal="center">
        <mds-col :cols="12">
          <div class="header-wrapper">
            <mds-row>
              <mds-col :cols="3">
                <mds-button
                  id="default-trigger"
                  variation="flat"
                  :icon-right="mainButtonIcon"
                  class="hypo-name"
                  @click="handleMainButtonAction"
                >
                  {{ componentConfig.mainText }}
                </mds-button>
                <mds-popover
                  v-model="toggleMenu"
                  title-hidden
                  title="Default"
                  triggered-by="default-trigger"
                  :position="['bottom-center']"
                  width="200px"
                >
                  <mds-list-group>
                    <mds-list-group-item
                      v-for="option in componentConfig.menuOptions"
                      :key="option.trigger"
                      :text="option.label"
                      @mds-list-item-clicked="itemClicked(option.trigger)"
                    ></mds-list-group-item>
                  </mds-list-group>
                </mds-popover>
              </mds-col>
              <mds-col :cols="6"> </mds-col>
              <mds-col :cols="3">
                <mds-button-container class="button-container">
                  <mds-button
                    v-if="ellipsisButtonConfig && ellipsisButtonConfig.isVisible"
                    id="ellipsis-trigger"
                    e2e="button-ellipsis"
                    class="button button-ellipsis"
                    type="button"
                    variation="icon-only"
                    icon="ellipsis-circle"
                    @click="ellipsisMenu = !ellipsisMenu"
                  >
                  </mds-button>
                  <mds-popover
                    v-if="ellipsisButtonConfig && ellipsisButtonConfig.menuOptions"
                    v-model="ellipsisMenu"
                    title-hidden
                    title="Default"
                    triggered-by="ellipsis-trigger"
                    :position="['bottom-center']"
                    width="200px"
                  >
                    <mds-list-group>
                      <mds-list-group-item
                        v-for="option in ellipsisButtonConfig.menuOptions"
                        :key="option.trigger"
                        :text="option.label"
                        @mds-list-item-clicked="itemClicked(option.trigger)"
                      ></mds-list-group-item>
                    </mds-list-group>
                  </mds-popover>
                  <mds-button
                    v-if="componentConfig.leftButton.isVisible"
                    e2e="left-button"
                    class="button button-left"
                    :variation="componentConfig.leftButton.type"
                    :disabled="!componentConfig.leftButton.isActive"
                    @click="leftButtonAction"
                  >
                    {{ componentConfig.leftButton.label }}
                  </mds-button>
                  <mds-button
                    v-if="componentConfig.middleButton.isVisible"
                    e2e="middle-button"
                    class="button button-middle"
                    :variation="componentConfig.middleButton.type"
                    :disabled="!componentConfig.middleButton.isActive"
                    @click="middleButtonAction"
                  >
                    {{ componentConfig.middleButton.label }}
                  </mds-button>
                  <mds-button
                    v-if="componentConfig.rightButton.isVisible"
                    e2e="right-button"
                    class="button button-right"
                    :variation="componentConfig.rightButton.type"
                    :disabled="!componentConfig.rightButton.isActive"
                    @click="rightButtonAction"
                  >
                    {{ componentConfig.rightButton.label }}
                  </mds-button>
                </mds-button-container>
              </mds-col>
            </mds-row>
          </div>
        </mds-col>
      </mds-row>
    </mds-layout-grid>
  </div>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import { MdsLayoutGrid, MdsRow, MdsCol } from '@mds/layout-grid';
import { MdsButton, MdsButtonContainer } from '@mds/button';
import MdsPopover from '@mds/popover';
import { MdsListGroup, MdsListGroupItem } from '@mds/list-group';
import defaultConfig from './_config';

export default {
  name: 'hypo-workflow-header',
  components: {
    MdsLayoutGrid,
    MdsRow,
    MdsCol,
    MdsButton,
    MdsButtonContainer,
    MdsPopover,
    MdsListGroup,
    MdsListGroupItem,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    mwcId: {
      type: String,
      required: true,
    },
    config: {
      type: [String, Object],
      default: () => {},
    },
    componentConfig: {
      type: [Object],
      default: () => {
        return {
          leftButton: {
            type: 'secondary',
            isActive: true,
            isVisible: true,
            label: 'Close',
            actionTrigger: 'close',
          },
          middleButton: {
            type: 'secondary',
            isActive: true,
            isVisible: true,
            label: 'Previous',
            actionTrigger: 'back',
          },
          rightButton: {
            type: 'primary',
            isActive: true,
            isVisible: true,
            label: 'Next',
            actionTrigger: 'continue',
          },
          mainText: 'Hypothetical: Untitled',
          menuOptions: [],
        };
      },
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    closeMenu: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      defaultConfig: {
        intlNamespace: this.mwcId,
        ...defaultConfig,
      },
      toggleMenu: false,
      ellipsisMenu: false,
      activateResponsive: false,
    };
  },
  computed: {
    mainButtonIcon() {
      return this.componentConfig.mainButton?.icon || 'angle-down';
    },
    mainButtonAction() {
      return this.componentConfig.mainButton?.action;
    },
    ellipsisButtonConfig() {
      return this.componentConfig.ellipsisButton;
    },
  },
  mounted() {
    this.resizeHandler();
    window.addEventListener('resize', this.resizeHandler, false);
  },
  methods: {
    handleMainButtonAction() {
      if (this.componentConfig.mainButton?.action) {
        this.$emit(`${this.componentConfig.mainButton.action}`);
      } else {
        this.toggleMenu = !this.toggleMenu;
      }
    },
    handleStepClicked(step) {
      this.$emit('step-clicked', step);
    },
    leftButtonAction() {
      this.$emit(`${this.componentConfig.leftButton.actionTrigger}`);
    },
    middleButtonAction() {
      this.$emit(`${this.componentConfig.middleButton.actionTrigger}`);
    },
    rightButtonAction() {
      this.$emit(`${this.componentConfig.rightButton.actionTrigger}`);
    },
    resizeHandler() {
      const viewportWidth =
        window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
      this.activateResponsive = viewportWidth <= 600;
    },
    itemClicked(action) {
      this.$emit(`${action}`);
      if (this.closeMenu) {
        this.ellipsisMenu = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import '~@mds/constants';
@import '~@mds/typography';
@import './styles/variables';

.#{$component-ns} {
  background-color: $mds-background-color-white;
  font-family: $mds-typography-font-family-sans-serif;
  font-weight: 300;
}
.workflow-stepper {
  opacity: 0;
}
.mds-stepper___hypo {
  padding: 30px 0 16px;
  position: relative;
}
.mds-stepper__active-step-summary___hypo {
  bottom: 40px;
}
.hypo-workflow-header {
  padding: 0 $mds-space-2-x;
  background-color: $mds-color-white;
  border-bottom: $mds-border-separator;
}

.header-wrapper {
  height: 75px;
  .hypo-name {
    display: inline-block;
    margin: $mds-space-1-x 0 $mds-space-1-x;
    color: $mds-text-color-primary;
    padding: 0;
    text-align: left;
    span {
      @include mds-level-5-heading();
    }
    .mds-button__icon___hypo {
      align-self: flex-start;
    }
  }
  .button-container {
    @include mds-level-5-heading();
    float: right;
    margin-top: $mds-space-1-x;
    // Temporary nav buttons responsive layout solution
    .button {
      max-width: 83px;
      transition: max-width 1.2s ease-out;
      &.button-ellipsis {
        margin-right: -8px;
      }
      &.button-middle {
        max-width: 105px;
      }
      &:hover {
        max-width: 200px;
      }
      span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      // Increase selector priority
      &.button {
        @media (min-width: 1000px) {
          max-width: 200px;
        }
      }
    }
  }
}
</style>
